import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrderByNumber } from '../utils/api';
import { FiCheckCircle, FiPackage, FiTruck } from 'react-icons/fi';
import './OrderSuccess.css';

// Base URL for serving static files (images)
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') return '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

const OrderSuccess = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOrder = useCallback(async () => {
    try {
      const data = await getOrderByNumber(orderNumber);
      setOrder(data.order);
    } catch (error) {
      // Order loading failed silently
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/60x60?text=No+Image';
    if (imageUrl.startsWith('/uploads/')) {
      return `${getBaseUrl()}${imageUrl}`;
    }
    return imageUrl;
  };

  if (loading) {
    return (
      <div className="order-success-page">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-success-page">
        <div className="container">
          <div className="success-card">
            <h2>Order not found</h2>
            <p>We couldn't find this order.</p>
            <Link to="/" className="btn btn-primary">Go to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-success-page">
      <div className="container">
        <div className="success-card animate-scale-in">
          <div className="success-icon">
            <FiCheckCircle />
          </div>
          <h1>Order Confirmed</h1>
          <p className="success-message">
            Thank you for your order. We'll send you a confirmation email shortly.
          </p>

          <div className="order-number-badge">
            Order #{order.orderNumber}
          </div>

          <div className="order-timeline">
            <div className="timeline-step completed">
              <div className="step-icon"><FiCheckCircle /></div>
              <span>Order Placed</span>
            </div>
            <div className="timeline-line"></div>
            <div className="timeline-step">
              <div className="step-icon"><FiPackage /></div>
              <span>Processing</span>
            </div>
            <div className="timeline-line"></div>
            <div className="timeline-step">
              <div className="step-icon"><FiTruck /></div>
              <span>Delivered</span>
            </div>
          </div>

          <div className="order-details">
            <h3>Order Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="label">Total Amount</span>
                <span className="value">₹{order.totalAmount.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="label">Payment Status</span>
                <span className="value status-paid">Paid</span>
              </div>
              <div className="detail-item full-width">
                <span className="label">Delivery Address</span>
                <span className="value">
                  {order.customerInfo.address.street}, {order.customerInfo.address.city}, {order.customerInfo.address.state} - {order.customerInfo.address.pincode}
                </span>
              </div>
            </div>
          </div>

          <div className="order-items">
            <h3>Items Ordered</h3>
            <div className="items-list">
              {order.items.map((item, index) => (
                <div key={index} className="order-item">
                  <img src={getImageUrl(item.productImage)} alt={item.productName} />
                  <div className="item-info">
                    <p className="item-name">{item.productName}</p>
                    <p className="item-quantity">Qty: {item.quantity}</p>
                  </div>
                  <p className="item-price">₹{(item.price * item.quantity).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="success-actions">
            <Link to="/shop" className="btn btn-primary">
              Continue Shopping
            </Link>
            <Link to="/" className="btn btn-outline">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
